import React, { useState, Fragment } from "react";
import { Heading, Message, Button, Columns } from "react-bulma-components";
import {
  Field as BulmaField,
  Control,
  Help
} from "react-bulma-components/lib/components/form";
import Link from "next/link";
import { colors, COLORS, APPLICATION_STATUS, FUNCTION, UNDEFINED } from "../../constants";
import * as Yup from "yup";
import { Formik, Field, Form } from "formik";
import Input from "../inputs/SimpleInputField";
import CheckboxInput from "../inputs/CheckboxInputField";
import { firebase, db, fieldValue } from "../../lib/firebase";
import UploadInput from "../inputs/UploadInput";
import { useAuth } from "../../lib/use-auth";
import { useDocument } from "@nandorojo/swr-firestore";
import pickBy from "lodash/pickBy";
import isNil from "lodash/isNil";
import negate from "lodash/negate";

const anonApplicationSchema = Yup.object().shape({
  isLink: Yup.boolean().default(true),
  resume: Yup.string("Problem uploading")
    .required("Required")
    .default(""),
  coverLetter: Yup.string("Problem uploading")
    .nullable()
    .default(""),
  otherJobs: Yup.boolean().default(false),
  user: Yup.object({
    uid: Yup.string().required(),
    displayName: Yup.string().nullable(),
    email: Yup.string()
      .email("Provide a valid email")
      .nullable(),
    phoneNumber: Yup.string().nullable()
  })
});

const termsLabel = (
  <Fragment>
    I agree to the{" "}
    <Link href="/policies/terms-of-service" passHref>
      <a>terms and conditions</a>
    </Link>{" "}
    and{" "}
    <Link href="/policies/terms-of-service" passHref>
      <a>privacy policy</a>
    </Link>
    .
  </Fragment>
);

const AnonymousApplication = ({ job, referral = null, ...props }) => {
  const { user } = useAuth();
  const companyData = useDocument(`/companies/${job.company.id}`);
  const company = companyData.data;

  if (!user) {
    return <p>Loading...</p>;
  }
  const [applied, setApplied] = useState(false);
  const applicationForm = (
    <Formik
      validationSchema={anonApplicationSchema}
      initialValues={{
        ...anonApplicationSchema.default(),
        referral,
        job: {
          id: job.id,
          headline: job.headline
        },
        company: job.company,
        user: {
          uid: user?.uid,
          name: user?.displayName,
          email: user?.email,
          phoneNumber: user?.phoneNumber
        }
      }}
      onSubmit={async (values, { setSubmitting, setErrors }) => {
        const valuesCopy = {
          ...pickBy(values, negate(isNil)),
          created: fieldValue.serverTimestamp(),
          updated: fieldValue.serverTimestamp(),
          isActive: true,
          status: APPLICATION_STATUS.applied.key
        };
        try {
          await db
            .collection(`jobs/${job.id}/applications`)
            .doc(`${user.uid}`)
            .set(valuesCopy); // merge: true causes bugs in counter update
          // https://github.com/firebase/extensions/blob/master/firestore-send-email/functions/src/index.ts
          if (typeof(window.fbq) === FUNCTION) {
            window.fbq("track", "SubmitApplication", {
              content_name: "Application",
              currency: "EUR",
              status: true,
              value: 0
            });
          }
          await db.collection(`mail`).add({
            toUids: [user.uid],
            template: {
              name: "job-applied-mail",
              data: { job, user: user.toJSON() }
            }
          });
          await db.collection(`mail`).add({
            toUids: Object.entries(company.admins)
              .filter(([key, value]) => value)
              .map(([key, value]) => key),
            template: {
              name: "application-received-mail",
              data: { company, job, user: user.toJSON() }
            }
          });
          setApplied(true);
          await db
            .doc(`jobs/${job.id}`)
            .update({ numApplications: fieldValue.increment(1) });
        } catch (error) {
          console.log(error);
          setErrors({
            server: error.toString()
          });
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isValid,
        isSubmitting,
        submitForm,
        setFieldValue
      }) => (
        <Form>
          <Columns>
            <Columns.Column size={9}>
              <BulmaField>
                {!values.isLink ? (
                  <UploadInput
                    name="resume"
                    accept="application/msword,text/plain,application/pdf"
                    multiple={false}
                    storageRef={"resumes"}
                    preview={true}
                    helpText={
                      "Required. In Word/PDF/Text formats smaller than 10 MB"
                    }
                  >
                    {values.resume ? "Change" : "Upload your CV"}
                  </UploadInput>
                ) : (
                  <Fragment>
                    <Input
                      name="resume"
                      label="Link to your profile"
                      placeholder={`https://linkedin.com/in/${values.user.name}`}
                    />
                    <Help>LinkedIn or other publicly available url</Help>
                  </Fragment>
                )}
              </BulmaField>
            </Columns.Column>
            <Columns.Column size={3}>
              <a
                href=""
                onClick={e => {
                  setFieldValue("isLink", !values.isLink);
                  setFieldValue("resume", "");
                  e.preventDefault();
                }}
              >
                {!values.isLink
                  ? "Paste link to your profile instead"
                  : "Upload CV instead"}
              </a>
            </Columns.Column>
          </Columns>
          <BulmaField>
            <UploadInput
              name="coverLetter"
              accept="application/msword,text/plain,application/pdf"
              multiple={false}
              storageRef={"coverLetters"}
              preview={true}
              helpText={"Optional. In Word/PDF/Text formats smaller than 10 MB"}
            >
              {values.coverLetter ? "Change" : "Upload your Cover Letter"}
            </UploadInput>
          </BulmaField>
          <Columns>
            <Columns.Column size="half">
              <Input name="user.name" label="Name" />
            </Columns.Column>
          </Columns>
          <Columns>
            <Columns.Column size="half">
              <Input name="user.email" label="Email" />
            </Columns.Column>
          </Columns>
          <Columns>
            <Columns.Column size="half">
              <Input name="user.phoneNumber" label="Phone Number" />
            </Columns.Column>
          </Columns>
          <CheckboxInput
            name="otherJobs"
            label="I'm interested to hear about other similar jobs. Let me know."
          />
          {errors.server ? (
            <Help color={colors.danger}>{errors.server}</Help>
          ) : null}
          <BulmaField>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
              color={COLORS.DARK}
            >
              Submit
            </Button>
          </BulmaField>
        </Form>
      )}
    </Formik>
  );

  const applicationSuccess = (
    <Message color={COLORS.SUCCESS}>
      <Message.Header>Submitted successfully.</Message.Header>
      <Message.Body>
        <Heading size={6}>Good job!</Heading>
        <br />
        To comply with GDPR, we sent you an email to confirm your consent. Your
        application will be visible to {job.company.name} only after you click
        the consent link in the email.
      </Message.Body>
    </Message>
  );

  return <Fragment>{applied ? applicationSuccess : applicationForm}</Fragment>;
};

export default AnonymousApplication;

// export default createFragmentContainer(AnonymousApplication, {
//   job: graphql`
//     fragment AnonymousApplication_job on JobNode {
//       id
//       company {
//         name
//       }
//     }
//   `
// });
